import { useEffect, useMemo, useState } from "react";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  ArrowUpRight,
  ChevronDown,
  Copy,
  Eye,
  GripVertical,
  Image as ImageIcon,
  LayoutGrid,
  Link2,
  Monitor,
  MousePointer2,
  Palette,
  Plus,
  Redo2,
  RotateCcw,
  Settings2,
  SlidersHorizontal,
  Sparkles,
  Trash2,
  Type,
  Undo2,
  X,
} from "lucide-react";
import { sectionLabel } from "../lib";
import { useEditor } from "../store";
import {
  defaultAnimation,
  defaultStyle,
  type AnimationName,
  type Item,
  type Section,
  type SectionType,
} from "../types";

export function ModeSwitch() {
  const { editing, setEditing } = useEditor();
  return (
    <div className="mode-switch">
      <button
        className={!editing ? "active" : ""}
        onClick={() => setEditing(false)}
      >
        <Eye size={15} /> View Site
      </button>
      <button
        className={editing ? "active" : ""}
        onClick={() => setEditing(true)}
      >
        <SlidersHorizontal size={15} /> Edit Site
      </button>
    </div>
  );
}

function Tooltip({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <span className="tooltip-wrap">
      {children}
      <span role="tooltip">{label}</span>
    </span>
  );
}

export function FloatingToolbar({ onReset }: { onReset: () => void }) {
  const {
    editing,
    preview,
    setPreview,
    undo,
    redo,
    past,
    future,
    setEditing,
    saveState,
  } = useEditor();
  if (!editing) return null;
  return (
    <div className="floating-toolbar" aria-label="Editor toolbar">
      <span className={`save-indicator ${saveState}`}>
        <i />
        {saveState === "saving"
          ? "Saving"
          : saveState === "offline"
            ? "Saved locally"
            : "Saved"}
      </span>
      <i className="toolbar-divider" />
      <Tooltip label="Undo · ⌘Z">
        <button onClick={undo} disabled={!past.length}>
          <Undo2 />
        </button>
      </Tooltip>
      <Tooltip label="Redo · ⇧⌘Z">
        <button onClick={redo} disabled={!future.length}>
          <Redo2 />
        </button>
      </Tooltip>
      <i className="toolbar-divider" />
      <Tooltip
        label={preview ? "Return to editor" : "Preview without controls"}
      >
        <button
          className={preview ? "active" : ""}
          onClick={() => setPreview(!preview)}
        >
          <Monitor />
        </button>
      </Tooltip>
      <Tooltip label="Restore the original demo">
        <button onClick={onReset}>
          <RotateCcw />
        </button>
      </Tooltip>
      <button className="exit-editor" onClick={() => setEditing(false)}>
        Exit editor <X />
      </button>
    </div>
  );
}

function Control({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="control">
      <span>{label}</span>
      {children}
    </label>
  );
}
function TextControl({
  label,
  value,
  onChange,
  multiline = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  multiline?: boolean;
}) {
  return (
    <Control label={label}>
      {multiline ? (
        <textarea
          value={value}
          maxLength={2000}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          value={value}
          maxLength={500}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </Control>
  );
}
function SliderControl({
  label,
  value,
  min,
  max,
  step = 1,
  onChange,
  suffix = "",
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
  suffix?: string;
}) {
  return (
    <Control label={label}>
      <div className="range-row">
        <input
          type="range"
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={(e) => onChange(Number(e.target.value))}
        />
        <output>
          {value}
          {suffix}
        </output>
      </div>
    </Control>
  );
}
function ColorControl({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <Control label={label}>
      <div className="color-row">
        <input
          type="color"
          value={value.startsWith("#") ? value : "#ffffff"}
          onChange={(e) => onChange(e.target.value)}
        />
        <input value={value} onChange={(e) => onChange(e.target.value)} />
      </div>
    </Control>
  );
}
function SelectControl({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) {
  return (
    <Control label={label}>
      <div className="select-wrap">
        <select value={value} onChange={(e) => onChange(e.target.value)}>
          {options.map((option) => (
            <option key={option} value={option}>
              {option.replaceAll("-", " ")}
            </option>
          ))}
        </select>
        <ChevronDown size={14} />
      </div>
    </Control>
  );
}

function AnimationControls({ section }: { section: Section }) {
  const update = useEditor((s) => s.updateSection);
  const set = <K extends keyof Section["animation"]>(
    key: K,
    value: Section["animation"][K],
  ) => {
    update(section.id, (target) => {
      target.animation[key] = value;
    });
    const node = document.getElementById(section.id);
    if (node) {
      const distance =
        key === "intensity" ? Number(value) : section.animation.intensity;
      const duration =
        (key === "duration" ? Number(value) : section.animation.duration) *
        1000;
      const name = key === "name" ? String(value) : section.animation.name;
      const from =
        name === "scale"
          ? { opacity: 0, transform: "scale(.92)" }
          : name === "slide"
            ? { opacity: 0, transform: `translateX(${distance}px)` }
            : name === "blur"
              ? { opacity: 0, filter: "blur(14px)" }
              : name === "clip"
                ? { clipPath: "inset(0 0 100% 0)" }
                : { opacity: 0, transform: `translateY(${distance}px)` };
      node.animate(
        [
          from,
          {
            opacity: 1,
            transform: "none",
            filter: "none",
            clipPath: "inset(0)",
          },
        ],
        { duration, easing: "cubic-bezier(.2,.8,.2,1)" },
      );
    }
  };
  return (
    <div className="panel-group">
      <h4>
        <Sparkles /> Animation
      </h4>
      <SelectControl
        label="Entrance"
        value={section.animation.name}
        options={[
          "none",
          "fade",
          "fade-up",
          "slide",
          "scale",
          "blur",
          "clip",
          "stagger",
          "spring",
        ]}
        onChange={(v) => set("name", v as AnimationName)}
      />
      <SliderControl
        label="Duration"
        value={section.animation.duration}
        min={0.1}
        max={2}
        step={0.05}
        suffix="s"
        onChange={(v) => set("duration", v)}
      />
      <SliderControl
        label="Delay"
        value={section.animation.delay}
        min={0}
        max={1}
        step={0.05}
        suffix="s"
        onChange={(v) => set("delay", v)}
      />
      <SliderControl
        label="Intensity"
        value={section.animation.intensity}
        min={0}
        max={80}
        suffix="px"
        onChange={(v) => set("intensity", v)}
      />
      <SelectControl
        label="Easing"
        value={section.animation.easing}
        options={["power3.out", "power2.inOut", "back.out", "elastic.out"]}
        onChange={(v) => set("easing", v)}
      />
    </div>
  );
}

function ItemControls({ section, item }: { section: Section; item: Item }) {
  const { updateItem, updateSection, select } = useEditor();
  const set = (key: keyof Item, value: string | number) =>
    updateItem(section.id, item.id, (target) => {
      (target as unknown as Record<string, string | number>)[key] = value;
    });
  const items = section.content.items as Item[];
  const remove = () =>
    updateSection(section.id, (target) => {
      target.content.items = (target.content.items as Item[]).filter(
        (entry) => entry.id !== item.id,
      );
      select({ sectionId: section.id, kind: "section" });
    });
  const duplicate = () =>
    updateSection(section.id, (target) => {
      const list = target.content.items as Item[];
      const index = list.findIndex((entry) => entry.id === item.id);
      list.splice(index + 1, 0, {
        ...item,
        id: crypto.randomUUID(),
        title: `${item.title} copy`,
      });
    });
  return (
    <>
      <div className="panel-group">
        <h4>
          <Type /> Content
        </h4>
        {item.label != null && (
          <TextControl
            label="Label"
            value={item.label}
            onChange={(v) => set("label", v)}
          />
        )}
        {item.icon != null && (
          <SelectControl
            label="Icon"
            value={item.icon}
            options={[
              "MousePointer2",
              "Zap",
              "ShieldCheck",
              "Globe2",
              "Sparkles",
            ]}
            onChange={(v) => set("icon", v)}
          />
        )}
        <TextControl
          label={section.type === "hours" ? "Opening time" : "Title"}
          value={item.title}
          onChange={(v) => set("title", v)}
        />
        {item.text != null && (
          <TextControl
            label={section.type === "hours" ? "Closing time" : "Text"}
            value={item.text}
            multiline={section.type !== "hours"}
            onChange={(v) => set("text", v)}
          />
        )}
        {item.category != null && (
          <TextControl
            label={section.type === "hours" ? "Optional note" : "Category"}
            value={item.category}
            onChange={(v) => set("category", v)}
          />
        )}
        {item.date != null && (
          <TextControl
            label="Date"
            value={item.date}
            onChange={(v) => set("date", v)}
          />
        )}
        {item.value != null && section.type === "hours" && (
          <SelectControl
            label="Status"
            value={item.value}
            options={["open", "closed"]}
            onChange={(v) => set("value", v)}
          />
        )}
      </div>
      {item.image != null && (
        <div className="panel-group">
          <h4>
            <ImageIcon /> Image
          </h4>
          <TextControl
            label="Image URL"
            value={item.image}
            onChange={(v) => set("image", v)}
          />
          <TextControl
            label="Alt text"
            value={item.alt ?? ""}
            onChange={(v) => set("alt", v)}
          />
          <SliderControl
            label="Focal point X"
            value={item.focalX ?? 50}
            min={0}
            max={100}
            suffix="%"
            onChange={(v) => set("focalX", v)}
          />
          <SliderControl
            label="Focal point Y"
            value={item.focalY ?? 50}
            min={0}
            max={100}
            suffix="%"
            onChange={(v) => set("focalY", v)}
          />
          <SliderControl
            label="Radius"
            value={item.radius ?? 0}
            min={0}
            max={80}
            suffix="px"
            onChange={(v) => set("radius", v)}
          />
          <SliderControl
            label="Overlay"
            value={item.overlay ?? 0}
            min={0}
            max={80}
            suffix="%"
            onChange={(v) => set("overlay", v)}
          />
        </div>
      )}
      <div className="item-actions">
        <button onClick={duplicate}>
          <Copy /> Duplicate
        </button>
        <button
          className="danger"
          onClick={remove}
          disabled={items.length <= 1}
        >
          <Trash2 /> Delete
        </button>
      </div>
    </>
  );
}

function SpecializedControls({ section }: { section: Section }) {
  const update = useEditor((s) => s.updateSection);
  const setContent = (key: string, value: string | number | boolean) =>
    update(section.id, (target) => {
      target.content[key] = value;
    });
  if (section.type === "hero")
    return (
      <div className="panel-group">
        <h4>
          <Type /> DepthText
        </h4>
        <TextControl
          label="Display text"
          value={String(section.content.title)}
          onChange={(v) => setContent("title", v)}
        />
        <SliderControl
          label="Layers"
          value={Number(section.content.layers)}
          min={2}
          max={64}
          onChange={(v) => setContent("layers", v)}
        />
        <SliderControl
          label="Depth"
          value={Number(section.content.depth)}
          min={0}
          max={12}
          step={0.1}
          suffix="px"
          onChange={(v) => setContent("depth", v)}
        />
        <ColorControl
          label="Face color"
          value={String(section.content.faceColor)}
          onChange={(v) => setContent("faceColor", v)}
        />
        <ColorControl
          label="Depth color"
          value={String(section.content.depthColor)}
          onChange={(v) => setContent("depthColor", v)}
        />
        <SliderControl
          label="Tilt"
          value={Number(section.content.tilt)}
          min={-20}
          max={20}
          suffix="°"
          onChange={(v) => setContent("tilt", v)}
        />
        <SliderControl
          label="Perspective"
          value={Number(section.content.perspective)}
          min={300}
          max={1600}
          suffix="px"
          onChange={(v) => setContent("perspective", v)}
        />
        <SliderControl
          label="Motion smoothing"
          value={Number(section.content.smoothing)}
          min={0.02}
          max={0.35}
          step={0.01}
          onChange={(v) => setContent("smoothing", v)}
        />
        <SliderControl
          label="Orbit speed"
          value={Number(section.content.orbitSpeed)}
          min={0}
          max={1}
          step={0.02}
          onChange={(v) => setContent("orbitSpeed", v)}
        />
        <SliderControl
          label="Font size"
          value={Number(section.content.fontSize)}
          min={54}
          max={150}
          suffix="px"
          onChange={(v) => setContent("fontSize", v)}
        />
        <SliderControl
          label="Font weight"
          value={Number(section.content.fontWeight)}
          min={400}
          max={900}
          step={10}
          onChange={(v) => setContent("fontWeight", v)}
        />
        <Control label="Pointer tracking">
          <input
            type="checkbox"
            checked={Boolean(section.content.pointerTracking)}
            onChange={(e) => setContent("pointerTracking", e.target.checked)}
          />
        </Control>
        <Control label="Automatic orbit">
          <input
            type="checkbox"
            checked={Boolean(section.content.orbit)}
            onChange={(e) => setContent("orbit", e.target.checked)}
          />
        </Control>
        <Control label="Depth shadow">
          <input
            type="checkbox"
            checked={Boolean(section.content.shadow)}
            onChange={(e) => setContent("shadow", e.target.checked)}
          />
        </Control>
      </div>
    );
  if (section.type === "pixel-swap")
    return (
      <div className="panel-group">
        <h4>
          <LayoutGrid /> PixelSwap
        </h4>
        <TextControl
          label="Before state"
          value={String(section.content.before)}
          multiline
          onChange={(v) => setContent("before", v)}
        />
        <TextControl
          label="After state"
          value={String(section.content.after)}
          multiline
          onChange={(v) => setContent("after", v)}
        />
        <SliderControl
          label="Pixel size"
          value={Number(section.content.pixelSize)}
          min={16}
          max={100}
          suffix="px"
          onChange={(v) => setContent("pixelSize", v)}
        />
        <SliderControl
          label="Gap"
          value={Number(section.content.gap)}
          min={0}
          max={10}
          suffix="px"
          onChange={(v) => setContent("gap", v)}
        />
        <SliderControl
          label="Pixel radius"
          value={Number(section.content.pixelRadius)}
          min={0}
          max={50}
          suffix="%"
          onChange={(v) => setContent("pixelRadius", v)}
        />
        <SliderControl
          label="Spin"
          value={Number(section.content.spin)}
          min={0}
          max={360}
          suffix="°"
          onChange={(v) => setContent("spin", v)}
        />
        <SliderControl
          label="Scale"
          value={Number(section.content.scale)}
          min={0.2}
          max={1.5}
          step={0.05}
          onChange={(v) => setContent("scale", v)}
        />
        <SliderControl
          label="Duration"
          value={Number(section.content.duration)}
          min={300}
          max={2400}
          step={50}
          suffix="ms"
          onChange={(v) => setContent("duration", v)}
        />
        <SliderControl
          label="Single pixel"
          value={Number(section.content.pixelDuration)}
          min={100}
          max={1000}
          step={25}
          suffix="ms"
          onChange={(v) => setContent("pixelDuration", v)}
        />
        <SliderControl
          label="Randomness"
          value={Number(section.content.randomness)}
          min={0}
          max={1}
          step={0.05}
          onChange={(v) => setContent("randomness", v)}
        />
        <SelectControl
          label="Pattern"
          value={String(section.content.pattern)}
          options={[
            "random",
            "center",
            "edges",
            "left-to-right",
            "right-to-left",
            "top-to-bottom",
            "bottom-to-top",
            "diagonal",
            "spiral",
          ]}
          onChange={(v) => setContent("pattern", v)}
        />
        <SelectControl
          label="Trigger"
          value={String(section.content.trigger)}
          options={["hover", "click"]}
          onChange={(v) => setContent("trigger", v)}
        />
      </div>
    );
  if (section.type === "gallery")
    return (
      <div className="panel-group">
        <h4>
          <ImageIcon /> AccordionGallery
        </h4>
        <SelectControl
          label="Orientation"
          value={String(section.content.orientation)}
          options={["horizontal", "vertical"]}
          onChange={(v) => setContent("orientation", v)}
        />
        <SliderControl
          label="Expand ratio"
          value={Number(section.content.expandRatio)}
          min={0.3}
          max={0.8}
          step={0.01}
          onChange={(v) => setContent("expandRatio", v)}
        />
        <Control label="Grayscale inactive panels">
          <input
            type="checkbox"
            checked={Boolean(section.content.grayscale)}
            onChange={(e) => setContent("grayscale", e.target.checked)}
          />
        </Control>
        <SliderControl
          label="Parallax"
          value={Number(section.content.parallax)}
          min={0}
          max={1.5}
          step={0.05}
          onChange={(v) => setContent("parallax", v)}
        />
        <SliderControl
          label="Tilt"
          value={Number(section.content.tilt)}
          min={0}
          max={10}
          suffix="°"
          onChange={(v) => setContent("tilt", v)}
        />
        <SelectControl
          label="Trigger"
          value={String(section.content.trigger)}
          options={["hover", "click"]}
          onChange={(v) => setContent("trigger", v)}
        />
        <SliderControl
          label="Timing"
          value={Number(section.content.timing)}
          min={0.1}
          max={1.5}
          step={0.05}
          suffix="s"
          onChange={(v) => setContent("timing", v)}
        />
      </div>
    );
  if (section.type === "scroll-expand")
    return (
      <div className="panel-group">
        <h4>
          <Monitor /> ScrollExpand
        </h4>
        <TextControl
          label="Title"
          value={String(section.content.title)}
          onChange={(v) => setContent("title", v)}
        />
        <TextControl
          label="Image URL"
          value={String(section.content.image)}
          onChange={(v) => setContent("image", v)}
        />
        <TextControl
          label="Alt text"
          value={String(section.content.alt)}
          onChange={(v) => setContent("alt", v)}
        />
        <SliderControl
          label="Start width"
          value={Number(section.content.startWidth)}
          min={35}
          max={95}
          suffix="%"
          onChange={(v) => setContent("startWidth", v)}
        />
        <SliderControl
          label="Start height"
          value={Number(section.content.startHeight)}
          min={35}
          max={90}
          suffix="vh"
          onChange={(v) => setContent("startHeight", v)}
        />
        <SliderControl
          label="Start radius"
          value={Number(section.content.startRadius)}
          min={0}
          max={80}
          suffix="px"
          onChange={(v) => setContent("startRadius", v)}
        />
        <SliderControl
          label="End radius"
          value={Number(section.content.endRadius)}
          min={0}
          max={80}
          suffix="px"
          onChange={(v) => setContent("endRadius", v)}
        />
        <SliderControl
          label="Media zoom"
          value={Number(section.content.mediaZoom)}
          min={1}
          max={1.5}
          step={0.01}
          onChange={(v) => setContent("mediaZoom", v)}
        />
        <SliderControl
          label="Overlay"
          value={Number(section.content.overlay)}
          min={0}
          max={80}
          suffix="%"
          onChange={(v) => setContent("overlay", v)}
        />
      </div>
    );
  if (section.type === "blog")
    return (
      <div className="panel-group">
        <h4>
          <LayoutGrid /> Blog layout
        </h4>
        <SelectControl
          label="Layout"
          value={section.layout}
          options={["grid", "list", "featured-grid", "horizontal-cards"]}
          onChange={(v) =>
            update(section.id, (target) => {
              target.layout = v;
            })
          }
        />
      </div>
    );
  return null;
}

export function ContextPanel() {
  const {
    site,
    selection,
    select,
    updateSection,
    updateItem,
    duplicateSection,
    deleteSection,
  } = useEditor();
  const [tab, setTab] = useState<"content" | "style">("content");
  const section = site.sections.find(
    (entry) => entry.id === selection?.sectionId,
  );
  const item =
    section && selection?.itemId
      ? (section.content.items as Item[] | undefined)?.find(
          (entry) => entry.id === selection.itemId,
        )
      : undefined;
  useEffect(() => setTab("content"), [selection?.sectionId, selection?.itemId]);
  if (!section)
    return (
      <aside className="context-panel empty">
        <div className="panel-empty-icon">
          <MousePointer2 />
        </div>
        <h3>Click anything</h3>
        <p>Select text, an image, card, or section directly on the page.</p>
        <span>ESC to deselect</span>
      </aside>
    );
  const setStyle = <K extends keyof Section["style"]>(
    key: K,
    value: Section["style"][K],
  ) =>
    updateSection(section.id, (target) => {
      target.style[key] = value;
    });
  const fieldValue = selection?.field
    ? String(
        item
          ? (item[selection.field as keyof Item] ?? "")
          : (section.content[selection.field] ?? ""),
      )
    : "";
  const setFieldValue = (value: string) =>
    selection?.field &&
    (item
      ? updateItem(section.id, item.id, (target) => {
          (target as unknown as Record<string, string>)[selection.field!] =
            value;
        })
      : updateSection(section.id, (target) => {
          target.content[selection.field!] = value;
        }));
  return (
    <aside className="context-panel">
      <div className="panel-title">
        <div>
          <small>{selection?.kind}</small>
          <strong>{item?.title || sectionLabel[section.type]}</strong>
        </div>
        <button onClick={() => select(null)}>
          <X />
        </button>
      </div>
      <div className="panel-tabs">
        <button
          className={tab === "content" ? "active" : ""}
          onClick={() => setTab("content")}
        >
          Content
        </button>
        <button
          className={tab === "style" ? "active" : ""}
          onClick={() => setTab("style")}
        >
          Style
        </button>
      </div>
      <div className="panel-scroll">
        {tab === "content" && (
          <>
            {item ? (
              <ItemControls section={section} item={item} />
            ) : selection?.kind === "text" && selection.field ? (
              <div className="panel-group">
                <h4>
                  <Type /> Text
                </h4>
                <TextControl
                  label="Content"
                  value={fieldValue}
                  multiline
                  onChange={setFieldValue}
                />
                <SliderControl
                  label="Font size"
                  value={Number(section.content[`${selection.field}Size`] ?? 0)}
                  min={0}
                  max={120}
                  suffix="px"
                  onChange={(v) =>
                    updateSection(section.id, (target) => {
                      target.content[`${selection.field}Size`] = v;
                    })
                  }
                />
                <SliderControl
                  label="Weight"
                  value={Number(
                    section.content[`${selection.field}Weight`] ?? 0,
                  )}
                  min={0}
                  max={900}
                  step={100}
                  onChange={(v) =>
                    updateSection(section.id, (target) => {
                      target.content[`${selection.field}Weight`] = v;
                    })
                  }
                />
                <ColorControl
                  label="Color override"
                  value={String(
                    section.content[`${selection.field}Color`] ??
                      section.style.color ??
                      site.theme.text,
                  )}
                  onChange={(v) =>
                    updateSection(section.id, (target) => {
                      target.content[`${selection.field}Color`] = v;
                    })
                  }
                />
                <div className="alignment-row">
                  {(
                    [
                      ["left", AlignLeft],
                      ["center", AlignCenter],
                      ["right", AlignRight],
                    ] as const
                  ).map(([value, Icon]) => (
                    <button
                      key={value}
                      className={section.style.align === value ? "active" : ""}
                      onClick={() => setStyle("align", value)}
                    >
                      <Icon />
                    </button>
                  ))}
                </div>
              </div>
            ) : selection?.kind === "image" ? (
              <div className="panel-group">
                <h4>
                  <ImageIcon /> Image
                </h4>
                <TextControl
                  label="Image URL"
                  value={String(section.content.image)}
                  onChange={(v) =>
                    updateSection(section.id, (target) => {
                      target.content.image = v;
                    })
                  }
                />
                <TextControl
                  label="Alt text"
                  value={String(section.content.alt)}
                  onChange={(v) =>
                    updateSection(section.id, (target) => {
                      target.content.alt = v;
                    })
                  }
                />
                <SliderControl
                  label="Focal point X"
                  value={Number(section.content.focalX ?? 50)}
                  min={0}
                  max={100}
                  suffix="%"
                  onChange={(v) =>
                    updateSection(section.id, (target) => {
                      target.content.focalX = v;
                    })
                  }
                />
                <SliderControl
                  label="Focal point Y"
                  value={Number(section.content.focalY ?? 50)}
                  min={0}
                  max={100}
                  suffix="%"
                  onChange={(v) =>
                    updateSection(section.id, (target) => {
                      target.content.focalY = v;
                    })
                  }
                />
                <SliderControl
                  label="Radius"
                  value={Number(section.content.imageRadius ?? 0)}
                  min={0}
                  max={80}
                  suffix="px"
                  onChange={(v) =>
                    updateSection(section.id, (target) => {
                      target.content.imageRadius = v;
                    })
                  }
                />
                <SliderControl
                  label="Overlay"
                  value={Number(section.content.imageOverlay ?? 0)}
                  min={0}
                  max={80}
                  suffix="%"
                  onChange={(v) =>
                    updateSection(section.id, (target) => {
                      target.content.imageOverlay = v;
                    })
                  }
                />
              </div>
            ) : (
              <SpecializedControls section={section} />
            )}
          </>
        )}
        {tab === "style" && (
          <>
            <div className="panel-group">
              <h4>
                <Palette /> Section style
              </h4>
              <ColorControl
                label="Background"
                value={section.style.background}
                onChange={(v) => setStyle("background", v)}
              />
              <ColorControl
                label="Text"
                value={section.style.color ?? site.theme.text}
                onChange={(v) => setStyle("color", v)}
              />
              <SliderControl
                label="Padding"
                value={section.style.padding}
                min={20}
                max={200}
                suffix="px"
                onChange={(v) => setStyle("padding", v)}
              />
              <SliderControl
                label="Radius"
                value={section.style.radius}
                min={0}
                max={64}
                suffix="px"
                onChange={(v) => setStyle("radius", v)}
              />
              <SliderControl
                label="Gap"
                value={section.style.gap}
                min={0}
                max={80}
                suffix="px"
                onChange={(v) => setStyle("gap", v)}
              />
              <SliderControl
                label="Columns"
                value={section.style.columns}
                min={1}
                max={4}
                onChange={(v) => setStyle("columns", v)}
              />
              <SliderControl
                label="Shadow"
                value={section.style.shadow}
                min={0}
                max={60}
                onChange={(v) => setStyle("shadow", v)}
              />
            </div>
            <SpecializedControls section={section} />
            <AnimationControls section={section} />
          </>
        )}
      </div>
      <div className="panel-footer">
        <button onClick={() => duplicateSection(section.id)}>
          <Copy /> Duplicate
        </button>
        <button
          className="danger"
          onClick={() => deleteSection(section.id)}
          disabled={site.sections.length <= 3}
        >
          <Trash2 /> Delete
        </button>
      </div>
    </aside>
  );
}

export function ThemePanel({ onClose }: { onClose: () => void }) {
  const { site, updateSite } = useEditor();
  const set = (key: keyof typeof site.theme, value: string | number) =>
    updateSite((target) => {
      (target.theme as unknown as Record<string, string | number>)[key] = value;
    });
  const updateNav = (id: string, key: "label" | "href", value: string) =>
    updateSite((target) => {
      const item = target.navigation.find((entry) => entry.id === id);
      if (item) item[key] = value;
    });
  const moveNav = (index: number, direction: number) =>
    updateSite((target) => {
      const to = index + direction;
      if (to < 0 || to >= target.navigation.length) return;
      const [item] = target.navigation.splice(index, 1);
      target.navigation.splice(to, 0, item);
    });
  return (
    <aside className="theme-panel">
      <div className="panel-title">
        <div>
          <small>GLOBAL</small>
          <strong>Theme</strong>
        </div>
        <button onClick={onClose}>
          <X />
        </button>
      </div>
      <div className="panel-scroll">
        <div className="panel-group">
          <h4>
            <Palette /> Color system
          </h4>
          {(
            [
              "background",
              "surface",
              "text",
              "muted",
              "primary",
              "secondary",
              "accent",
              "buttonText",
              "border",
            ] as const
          ).map((key) => (
            <ColorControl
              key={key}
              label={key.replace(/([A-Z])/g, " $1")}
              value={site.theme[key] as string}
              onChange={(v) => set(key, v)}
            />
          ))}
        </div>
        <div className="panel-group">
          <h4>
            <Settings2 /> Shape
          </h4>
          <div className="radius-presets">
            {[
              ["Sharp", 0],
              ["Soft", 12],
              ["Rounded", 22],
              ["Pill", 40],
            ].map(([label, value]) => (
              <button
                key={label}
                className={site.theme.radius === value ? "active" : ""}
                onClick={() => set("radius", value as number)}
              >
                {label}
              </button>
            ))}
          </div>
          <SliderControl
            label="Global radius"
            value={site.theme.radius}
            min={0}
            max={48}
            suffix="px"
            onChange={(v) => set("radius", v)}
          />
        </div>
        <div className="panel-group">
          <h4>
            <Link2 /> Navigation
          </h4>
          {site.navigation.map((item, index) => (
            <div className="nav-control" key={item.id}>
              <TextControl
                label="Label"
                value={item.label}
                onChange={(v) => updateNav(item.id, "label", v)}
              />
              <TextControl
                label="Link"
                value={item.href}
                onChange={(v) => updateNav(item.id, "href", v)}
              />
              <div>
                <button
                  disabled={index === 0}
                  onClick={() => moveNav(index, -1)}
                >
                  ↑
                </button>
                <button
                  disabled={index === site.navigation.length - 1}
                  onClick={() => moveNav(index, 1)}
                >
                  ↓
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </aside>
  );
}

const starterItems: Record<string, Item[]> = {
  services: [
    {
      id: crypto.randomUUID(),
      icon: "Sparkles",
      label: "01",
      title: "A new service",
      text: "Describe what makes this useful.",
    },
  ],
  stats: [
    {
      id: crypto.randomUUID(),
      value: "42",
      title: "A meaningful number",
      text: "Give it some context.",
    },
  ],
  testimonials: [
    {
      id: crypto.randomUUID(),
      title: "“Add a memorable client quote.”",
      text: "Name, company",
    },
  ],
  blog: [
    {
      id: crypto.randomUUID(),
      category: "News",
      date: "Today",
      title: "A new story",
      text: "Write a short, useful excerpt.",
      image: "/images/team.avif",
      alt: "Creative studio",
    },
  ],
  faq: [
    {
      id: crypto.randomUUID(),
      title: "A common question?",
      text: "A clear, reassuring answer.",
    },
  ],
};
export function AddMenu({ onClose }: { onClose: () => void }) {
  const addSection = useEditor((s) => s.addSection);
  const choices: Array<[SectionType, string, string?]> = [
    ["content", "Text", "text"],
    ["content", "Image", "image"],
    ["content", "Button", "button"],
    ["services", "Cards"],
    ["stats", "Statistic"],
    ["testimonials", "Testimonial"],
    ["gallery", "Gallery"],
    ["blog", "Blog post"],
    ["faq", "FAQ"],
    ["cta", "CTA"],
  ];
  const add = (type: SectionType, variant = "") => {
    const section: Section = {
      id: `${type}-${crypto.randomUUID()}`,
      type,
      content: {
        kicker: "NEW SECTION",
        title:
          variant === "text"
            ? "A new text block"
            : variant === "image"
              ? "A new image story"
              : variant === "button"
                ? "Ready when you are."
                : `Your new ${sectionLabel[type]}`,
        intro: "Click any text to edit it.",
        image: "/images/team.avif",
        alt: "Creative studio",
        button: "Learn more",
        showImage: variant === "image",
        showButton: variant === "button",
        items: starterItems[type] ?? [],
      },
      style: defaultStyle(type === "cta" ? "#ff5c35" : "#fffdf8"),
      layout: variant === "image" ? "split" : "stack",
      animation: defaultAnimation(),
    };
    addSection(section);
    onClose();
    setTimeout(
      () =>
        document
          .getElementById(section.id)
          ?.scrollIntoView({ behavior: "smooth", block: "center" }),
      50,
    );
  };
  return (
    <div className="add-popover">
      <div>
        <span>Add to page</span>
        <button onClick={onClose}>
          <X />
        </button>
      </div>
      <p>Choose a block. You can edit it immediately.</p>
      <div className="add-grid">
        {choices.map(([type, label, variant]) => (
          <button key={`${type}-${label}`} onClick={() => add(type, variant)}>
            <Plus />
            <span>{label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function EditorRail({ onTheme }: { onTheme: () => void }) {
  const { site, selection, select, reorderSections } = useEditor();
  const [drag, setDrag] = useState<string | null>(null);
  return (
    <nav className="editor-rail" aria-label="Page structure">
      <div className="rail-brand">
        Z<span>h</span>
      </div>
      <button onClick={onTheme}>
        <Palette />
        <span>Theme</span>
      </button>
      <i />
      {site.sections.map((section) => (
        <button
          key={section.id}
          draggable
          onDragStart={() => setDrag(section.id)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => {
            if (drag) reorderSections(drag, section.id);
            setDrag(null);
          }}
          className={selection?.sectionId === section.id ? "active" : ""}
          onClick={() => {
            select({ sectionId: section.id, kind: "section" });
            document
              .getElementById(section.id)
              ?.scrollIntoView({ behavior: "smooth", block: "center" });
          }}
        >
          <GripVertical />
          <span>{sectionLabel[section.type]}</span>
        </button>
      ))}
    </nav>
  );
}

export function EditorAddButton({ onClick }: { onClick: () => void }) {
  return (
    <button className="editor-add" onClick={onClick}>
      <Plus /> Add
    </button>
  );
}

export function ConfirmReset({
  onConfirm,
  onCancel,
}: {
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onCancel}>
      <div
        className="confirm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reset-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="modal-icon">
          <RotateCcw />
        </div>
        <h2 id="reset-title">Reset the whole demo?</h2>
        <p>
          Every change in this session will be replaced with the original
          ZhStudio design. You can undo the reset afterward.
        </p>
        <div>
          <button onClick={onCancel}>Keep editing</button>
          <button className="danger-fill" onClick={onConfirm}>
            Reset Demo
          </button>
        </div>
      </div>
    </div>
  );
}

export function Onboarding() {
  const dismiss = useEditor((s) => s.dismissOnboarding);
  const [step, setStep] = useState(0);
  const steps = useMemo(
    () => [
      {
        icon: MousePointer2,
        title: "Click anything",
        text: "Select text, images or sections directly.",
      },
      {
        icon: Palette,
        title: "Make it yours",
        text: "Change content, colors, layouts and animations.",
      },
      {
        icon: RotateCcw,
        title: "You can’t break it",
        text: "Use Undo or Reset Demo whenever you want.",
      },
    ],
    [],
  );
  const current = steps[step],
    Icon = current.icon;
  return (
    <div className="onboarding">
      <button className="skip" onClick={dismiss}>
        Skip
      </button>
      <div className="onboarding-visual">
        <div>
          <Icon />
          <span>{step + 1}</span>
        </div>
      </div>
      <small>QUICK TOUR · {step + 1} OF 3</small>
      <h2>{current.title}</h2>
      <p>{current.text}</p>
      <div className="onboarding-bottom">
        <div>
          {steps.map((_, i) => (
            <i key={i} className={i === step ? "active" : ""} />
          ))}
        </div>
        <button onClick={() => (step < 2 ? setStep(step + 1) : dismiss())}>
          {step < 2 ? "Next" : "Start editing"} <ArrowUpRight />
        </button>
      </div>
    </div>
  );
}
